import React, { useEffect, useState } from 'react';
import { Admin, Resource, CustomRoutes } from 'react-admin';
import { Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material';
import { CssBaseline } from '@mui/material';
import { logger, LogCategory } from './utils/logger';
import {
  People as UsersIcon,
  LocationOn as LocationsIcon,
  RateReview as PlayerReviewsIcon,
  Notifications as NotificationsIcon,
  Devices as DeviceTokensIcon,
  List as LogsIcon,
  Chat as ChatIcon,
  HowToReg as TrainerIcon,
  Event as SessionsIcon,
  SportsSoccer as SportsIcon,
  VerifiedUser as SpecialtyCertificatesIcon,
} from '@mui/icons-material';

// Import providers
import { dataProvider } from './dataProvider';
import { authProvider } from './authProvider';
import { AuthService } from './services/AuthService';

// Import Berry theme
import { berryTheme } from './theme';

// Import resources
import { UserList, UserShow, UserEdit, UserCreate } from './resources/users';
import { TrainerApplicationList, TrainerApplicationShow } from './resources/trainerApplications';
import { SpecialtyCertificateList, SpecialtyCertificateShow } from './resources/specialtyCertificates';
import { TrainerList, TrainerShow } from './resources/trainers';
import { LocationList, LocationShow, LocationEdit, LocationCreate } from './resources/locations';
import { SessionReviewList, SessionReviewShow } from './resources/reviews';
import { NotificationList, NotificationShow, NotificationEdit, NotificationCreate } from './resources/notifications';
import { NotificationLogsList, NotificationLogsShow } from './resources/notification-logs';
import { DeviceTokensList, DeviceTokensShow } from './resources/device-tokens';
import { SessionChatList, SessionChatShow } from './resources/session-chats';
import { DirectChatList, DirectChatShow } from './resources/direct-chats';
import { ChatMonitoringPage } from './components/routes/ChatMonitoringPage';
import { SettingsPage } from './resources/settings';
import { SessionList, SessionShow } from './resources/sessions';
import { BookingList, BookingShow } from './resources/bookings';
import { PaymentList, PaymentShow } from './resources/payments';
import { PayoutList } from './resources/payouts';
import { RefundList } from './resources/refunds';
import { WebhookList, WebhookShow } from './resources/payment-webhooks';
import { AppVersionsPage } from './resources/app-versions';
import { SportList, SportShow, SportEdit, SportCreate } from './resources/sports';
import { SystemHealthPage } from './resources/system-health';
import { AccountPage } from './resources/account';
import { NotificationCenter } from './components/notifications/NotificationCenter';

// Import dashboard, layout, and auth
import Dashboard from './Dashboard';
import { AppLayout } from './components/layout/AppLayout';
import { LoginPage } from './components/auth/LoginPage';
import { CustomErrorPage } from './components/admin/CustomErrorPage';

// Dashboard context is now imported in AppLayout
import { ErrorBoundary } from './components/common/ErrorBoundary';
import LoadingScreen from './components/common/LoadingScreen';
// Initialize error logger - import has side effects
import './utils/errorLogger';
import { BreadcrumbProvider } from './components/layout/AppBreadcrumb';

function App() {
  const [isReady, setIsReady] = useState(false);
  const [showLoading, setShowLoading] = useState(true);

  // Initialize services and wait for environment to be ready
  useEffect(() => {
    logger.debug(LogCategory.UI, 'App initialization started', undefined, 'App');

    let attempts = 0;
    const maxAttempts = 20; // Maximum 10 seconds (20 * 500ms)

    const checkReadiness = () => {
      attempts++;

      // Check if DOM and basic environment are ready
      const isDOMReady = document.readyState === 'complete' || document.readyState === 'interactive';
      const isLocationReady = window.location && window.location.href;
      const hasBasicEnv = typeof window !== 'undefined' && typeof document !== 'undefined';

      if (isDOMReady && isLocationReady && hasBasicEnv) {
        logger.info(LogCategory.UI, 'Environment checks passed, initializing services', undefined, 'App');

        try {
          AuthService.startTokenMonitoring();

          // Log app startup for production debugging
          logger.info(LogCategory.UI, 'Latio Dashboard starting', {
            timestamp: new Date().toISOString(),
            nodeEnv: process.env.NODE_ENV,
            attempts: attempts,
          }, 'App');

          setIsReady(true);
        } catch (error) {
          logger.error(LogCategory.UI, 'Error during initialization', error, 'App');
          // Still set ready to true to avoid infinite loading
          setIsReady(true);
        }
      } else if (attempts >= maxAttempts) {
        logger.warn(LogCategory.UI, 'Max readiness attempts reached, proceeding anyway', { attempts }, 'App');
        setIsReady(true);
      } else {
        logger.debug(LogCategory.UI, 'Environment not ready, retrying', { attempts, maxAttempts }, 'App');
        setTimeout(checkReadiness, 500);
      }
    };

    // Start checking after initial delay
    const timer = setTimeout(checkReadiness, 500);

    return () => clearTimeout(timer);
  }, []);

  // Minimum loading time to properly show the animation
  useEffect(() => {
    const minLoadingTimer = setTimeout(() => {
      setShowLoading(false);
    }, 1500); // Show loading for at least 1.5 seconds

    return () => clearTimeout(minLoadingTimer);
  }, []);

  if (!isReady || showLoading) {
    return <LoadingScreen />;
  }

  return (
    <ErrorBoundary>
      <BreadcrumbProvider>
        <ThemeProvider theme={berryTheme}>
          <CssBaseline />
          <Admin
            dataProvider={dataProvider}
            authProvider={authProvider}
            dashboard={Dashboard}
            layout={AppLayout}
            loginPage={LoginPage}
            error={CustomErrorPage}
            title="Latio Dashboard"
            theme={berryTheme}
          >
          {/* Core Resources */}
          <Resource
            name="users"
            list={UserList}
            show={UserShow}
            edit={UserEdit}
            create={UserCreate}
            icon={UsersIcon}
          />
          <Resource
            name="trainer-applications"
            list={TrainerApplicationList}
            show={TrainerApplicationShow}
            icon={TrainerIcon}
            options={{ label: 'Trainer Applications' }}
          />
          <Resource
            name="specialty-certificates"
            list={SpecialtyCertificateList}
            show={SpecialtyCertificateShow}
            icon={SpecialtyCertificatesIcon}
            options={{ label: 'Specialty Certificates' }}
          />
          <Resource
            name="trainers"
            list={TrainerList}
            show={TrainerShow}
            icon={TrainerIcon}
            options={{ label: 'Trainers' }}
          />
          <Resource
            name="locations"
            list={LocationList}
            show={LocationShow}
            edit={LocationEdit}
            create={LocationCreate}
            icon={LocationsIcon}
          />
          <Resource
            name="sports"
            list={SportList}
            show={SportShow}
            edit={SportEdit}
            create={SportCreate}
            icon={SportsIcon}
            options={{ label: 'Sports' }}
          />

          {/* Sessions Resource */}
          <Resource
            name="sessions"
            list={SessionList}
            show={SessionShow}
            icon={SessionsIcon}
            options={{ label: 'Sessions' }}
          />

          {/* Bookings Resource */}
          <Resource
            name="bookings"
            list={BookingList}
            show={BookingShow}
            options={{ label: 'Bookings' }}
          />

          {/* Payments Resources */}
          <Resource
            name="payments"
            list={PaymentList}
            show={PaymentShow}
            options={{ label: 'Payments' }}
          />
          <Resource
            name="payouts"
            list={PayoutList}
            options={{ label: 'Payouts' }}
          />
          <Resource
            name="refunds"
            list={RefundList}
            options={{ label: 'Refunds' }}
          />
          <Resource
            name="payment-webhooks"
            list={WebhookList}
            show={WebhookShow}
            options={{ label: 'Payment Webhooks' }}
          />

          {/* Review Resources */}
          <Resource
            name="session-reviews"
            list={SessionReviewList}
            show={SessionReviewShow}
            icon={PlayerReviewsIcon}
            options={{ label: 'Session Reviews' }}
          />

          {/* Notification Management Resources */}
          <Resource
            name="notifications"
            list={NotificationList}
            show={NotificationShow}
            edit={NotificationEdit}
            create={NotificationCreate}
            icon={NotificationsIcon}
            options={{ label: 'Notifications' }}
          />
          <Resource
            name="notification-logs"
            list={NotificationLogsList}
            show={NotificationLogsShow}
            icon={LogsIcon}
            options={{ label: 'Notification Logs' }}
          />
          <Resource
            name="device-tokens"
            list={DeviceTokensList}
            show={DeviceTokensShow}
            icon={DeviceTokensIcon}
            options={{ label: 'Device Tokens' }}
          />

          {/* Chat Management Resources */}
          <Resource
            name="session-chats"
            list={SessionChatList}
            show={SessionChatShow}
            icon={ChatIcon}
            options={{ label: 'Session Chats' }}
          />
          <Resource
            name="direct-chats"
            list={DirectChatList}
            show={DirectChatShow}
            options={{ label: 'Direct Chats' }}
          />

          {/* System Resources */}
          <Resource name="password-resets" />
          <Resource name="email-verifications" />
          <Resource name="chat-monitoring" />

          {/* Custom Routes */}
          <CustomRoutes>
            <Route path="/activity" element={<NotificationCenter />} />
            <Route path="/chat-monitoring" element={<ChatMonitoringPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/app-versions" element={<AppVersionsPage />} />
            <Route path="/system-health" element={<SystemHealthPage />} />
            <Route path="/account" element={<AccountPage />} />
          </CustomRoutes>
        </Admin>
        </ThemeProvider>
      </BreadcrumbProvider>
    </ErrorBoundary>
  );
}

export default App;
